import { getSSToken } from '../utils/token';

const FILTER_STATUS = ['MISE EN SERVICE', 'FIN DE MISE EN SERVICE'] as const;

interface MapApiValidationState {
    isValid: boolean;
    isChecking: boolean;
    lastChecked: number;
    error: string | null;
}

interface MapApiValidationSubscriber {
    id: string;
    callback: (state: MapApiValidationState) => void;
}

/**
 * Global service to manage map API validation state
 * Prevents duplicate requests when multiple components need to validate the API
 */
class MapApiValidationService {
    private state: MapApiValidationState = {
        isValid: false,
        isChecking: true,
        lastChecked: 0,
        error: null,
    };

    private subscribers: MapApiValidationSubscriber[] = [];
    private intervalId: NodeJS.Timeout | null = null;
    private readonly CACHE_DURATION = 30000; // 30 seconds cache
    private readonly POLL_INTERVAL = 3000; // 3 seconds polling

    /**
     * Subscribe to validation state changes
     */
    subscribe(callback: (state: MapApiValidationState) => void): string {
        const id = Math.random().toString(36).substring(7);
        this.subscribers.push({ id, callback });
        
        // Immediately notify with current state
        callback(this.state);
        
        // Start validation if not already running
        this.startValidation();
        
        return id;
    }

    /**
     * Unsubscribe from validation state changes
     */
    unsubscribe(id: string): void {
        this.subscribers = this.subscribers.filter(sub => sub.id !== id);
        
        // Stop validation if no more subscribers
        if (this.subscribers.length === 0) {
            this.stopValidation();
        }
    }

    /**
     * Start validation process
     */
    private startValidation(): void {
        if (this.intervalId) return; // Already running

        // Check if we have cached valid data
        const now = Date.now();
        if (this.state.isValid && (now - this.state.lastChecked) < this.CACHE_DURATION) {
            // Use cached data, but still start polling for future updates
            this.scheduleNextCheck();
            return;
        }

        // Start immediate validation
        this.checkApiValidity();
        
        // Schedule regular polling
        this.scheduleNextCheck();
    }

    /**
     * Stop validation process
     */
    private stopValidation(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }

    /**
     * Schedule next validation check
     */
    private scheduleNextCheck(): void {
        this.intervalId = setInterval(() => {
            this.checkApiValidity();
        }, this.POLL_INTERVAL);
    }

    /**
     * Check API validity
     */
    private async checkApiValidity(): Promise<void> {
        // Update checking state
        this.updateState({ isChecking: true });

        try {
            const token = await getSSToken();
            if (!token) {
                this.updateState({ 
                    isValid: false, 
                    isChecking: false,
                    error: 'No token available',
                    lastChecked: Date.now()
                });
                return;
            }

            const apiBase = process.env.REACT_APP_API_SS_ENTRYPOINT || '';
            const base = apiBase.endsWith('/') ? apiBase.slice(0, -1) : apiBase;
            const urlBase = `${base}/rikutec/source_boards`;

            const searchParams = new URLSearchParams();
            searchParams.set('page', '1');
            searchParams.set('itemsPerPage', '1');
            searchParams.set('productCurrentStatusName[0]', FILTER_STATUS[0]);
            searchParams.set('productCurrentStatusName[1]', FILTER_STATUS[1]);
            searchParams.set('exists[latitude]', 'true');
            searchParams.set('exists[longitude]', 'true');

            const requestUrl = `${urlBase}?${searchParams.toString()}`;
            const response = await fetch(requestUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`.trim(),
                },
            });

            const isValid = response.ok;
            this.updateState({ 
                isValid, 
                isChecking: false,
                error: isValid ? null : `API validation failed: ${response.status}`,
                lastChecked: Date.now()
            });

            // Stop polling once API is valid
            if (isValid && this.intervalId) {
                clearInterval(this.intervalId);
                this.intervalId = null;
            }

        } catch (error) {
            console.error('Error checking map API validity:', error);
            this.updateState({ 
                isValid: false, 
                isChecking: false,
                error: error instanceof Error ? error.message : 'Unknown error',
                lastChecked: Date.now()
            });
        }
    }

    /**
     * Update state and notify all subscribers
     */
    private updateState(updates: Partial<MapApiValidationState>): void {
        this.state = { ...this.state, ...updates };
        
        // Notify all subscribers
        this.subscribers.forEach(subscriber => {
            try {
                subscriber.callback(this.state);
            } catch (error) {
                console.warn('Error notifying map API validation subscriber:', error);
            }
        });
    }

    /**
     * Get current state (for debugging)
     */
    getState(): MapApiValidationState {
        return { ...this.state };
    }

    /**
     * Force refresh validation (ignore cache)
     */
    forceRefresh(): void {
        this.state.lastChecked = 0; // Force cache invalidation
        this.checkApiValidity();
    }
}

// Export singleton instance
export const mapApiValidationService = new MapApiValidationService();
export type { MapApiValidationState };

